// LG a11y R7 (§3.10 / §3.2) — cart FOCUS-AND-ANNOUNCE contract.
//
// Horizon announces ONLY the total cart count on a cart change (header-actions.js:60 →
// "Total items in cart: N"); it never announces WHICH item was removed or that a line's quantity
// was updated, and it never moves focus when a mutation deletes the control the user just
// activated. WCAG 4.1.3 (§3.10) requires the announcement; WCAG 2.4.3 (§3.2) requires the focus
// move. They are ONE contract: announcing a removal while stranding focus on <body> — inside a
// still-open modal drawer — still fails 2.4.3, so this module owns both halves together.
//
// ── Announcement ────────────────────────────────────────────────────────────────────────────
// This controller captures the affected line's product title (and new quantity) at INTERACTION
// time — before the cart-items morph wipes the row — then announces the semantic once the
// mutation reaches `StandardEvents.cartLinesUpdate`. Zero vendor edit: it rides the same event
// mesh cart-items-component already dispatches (§4.5, §4.15 — skin/extend, don't compete).
//
// The latch rides `pointerdown` / `keydown` on DOCUMENT, NOT `click` (horizon-mechanics §4.59).
// Horizon's `on:*` bindings are delegated from a single document-CAPTURE listener
// (component.js:218-232 + :322), and `cart-items-component.updateQuantity` dispatches
// CartLinesUpdateEvent SYNCHRONOUSLY inside it — so any LG click listener, at any depth, runs
// strictly AFTER the event it is supposed to inform (a `click` latch is always one mutation late:
// silence on the mutation it was meant for, then a stale mis-announce on the next one).
// `pointerdown` and `keydown` are SEPARATE EARLIER EVENTS, so their precedence is temporal, not
// listener-order — nothing Horizon registers later can get in front of them.
// `document` placement is required by the LINES path (the ordering argument above). The discount
// path never uses the latch — `#onDiscountUpdate` diffs `event.discountCodes` against the rendered
// pills, and `CartDiscountUpdateEvent` bubbles to `document` — so it is merely convenient there.
// (cart-discount.js stopPropagation()s its click :180 and submit :43, so a component-scoped
// listener could not observe those interactions anyway.)
//
// ── Focus ───────────────────────────────────────────────────────────────────────────────────
// A cart mutation deletes the control the user activated. `#guardFocus` re-homes focus on a
// meaningful surviving counterpart, ORPHAN-GUARDED (it acts only while focus has fallen to
// <body>, so it never yanks a shopper who deliberately tabbed away mid-flight) and RE-ARMING
// (Horizon mutates twice per removal — an optimistic local `row.remove()` / empty-template swap,
// then the server morph — and the morph's reorder path can blur a just-restored node;
// morph.js:628-642 MOVES a live node with insertBefore when ids shift). Targets are re-located in
// the live DOM by key, never held as element references (§4.14).
//
// The polite live region lives in the drawer shell OUTSIDE `.cart-drawer__inner[data-hydration-key]`
// (lg-cart.liquid), so the drawer's hydration-mode morph never re-renders or clears it.
// Copy is locale-driven via data-* (merchant-safe), never hardcoded.
//
// KNOWN LIMIT: outside the morph target is not outside the DIALOG. This element renders inside
// <cart-drawer-component> inside the <dialog> (snippets/lg-cart-drawer.liquid), and a closed
// <dialog> is `display: none` by UA stylesheet — so a message written AFTER the drawer closes is
// conveyed by no screen reader. Two paths remain silent: a shopper pressing Escape mid-flight, and
// the GWP direct-announce seam firing while the drawer is shut. (The reduced-motion removal that
// used to close the drawer under us is fixed by the isClickedOutside guard in utilities.js,
// horizon-mechanics §4.60.) Hosting the region OUTSIDE the <dialog> is REFUTED: the drawer is
// `data-modal="always"`, so showModal() makes everything outside it inert. The planned fix is two
// regions, one write, chosen at write time by `panel.matches(':modal')` — see
// tasks/bugs/2026-10-14-cart-announce-region-inside-dialog.md.

import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';

/** The drawer's sole morph target — also present (inert) on the /cart page (§4.43). */
const INNER = '[data-hydration-key="cart-drawer-inner"]';

/** Longest window we will keep re-homing focus after a mutation starts. */
const GUARD_MS = 2000;

/** Grace after the mutation promise settles, to cover the morph + the row animation. */
const SETTLE_TAIL_MS = 400;

/**
 * @typedef {Object} LgCartAnnounceRefs
 * @property {HTMLElement} liveRegion
 */

/**
 * @typedef {Object} Pending
 * @property {'remove' | 'update' | 'bundle'} action
 * @property {string} product
 * @property {string} [quantity]
 * @property {string[]} keys - cart line keys this interaction mutates (verified against the event).
 * @property {string | null} [nextKey] - key of the row that will take the removed row's place.
 * @property {string | null} [prevKey] - key of the row above the removed row.
 * @property {boolean} [wasLast] - the removal takes every line the DOM showed (cart will be empty).
 */

/**
 * @extends {Component<LgCartAnnounceRefs>}
 */
class LgCartAnnounce extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {Pending | null} */
  #pending = null;

  /** @type {{ stop: () => void } | null} */
  #guard = null;

  connectedCallback() {
    super.connectedCallback();
    // DOCUMENT, capture, on the events that PRECEDE click (§4.59). Handlers are stable
    // instance properties, so a re-connect (the /cart page morphs this element's host) is
    // idempotent — addEventListener de-dupes identical (type, listener, capture) triples.
    document.addEventListener('pointerdown', this.#onInteract, true);
    // A press that turns into a scroll/pan (touch starting on a Remove X) never clicks: the
    // browser fires pointercancel instead. Drop the latch so no later mutation consumes it.
    document.addEventListener('pointercancel', this.#onAnnounceCancel, true);
    document.addEventListener('keydown', this.#onKeyDown, true);
    // Quantity changes ride the bubbling QuantitySelectorUpdateEvent, NOT native `change`:
    // the +/- steppers set input.value programmatically (no native change fires), and the
    // typed-then-blur path also routes through onQuantityChange → the same custom event.
    // Listening on the custom event covers BOTH paths (code-review B1, 2026-08-28). It is
    // dispatched BEFORE the debounced vendor handler, so no §4.59 ordering problem here.
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQtyUpdate);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#onDiscountUpdate);
    // A bundle removal can be abandoned before it dispatches anything (no keys, or the
    // re-entry guard). The press already latched #pending, so without this the next
    // unrelated cart update would consume it and announce a removal that never happened.
    document.addEventListener('lg:cart:announce-cancel', this.#onAnnounceCancel);
    // Focus-restore seam. assets/lg-cart-bundle.js hands its atomic multi-key removal over
    // here rather than keeping a private near-copy of the restore helper.
    document.addEventListener('lg:cart:restore-focus', this.#onRestoreFocus);
    // Direct-announce seam (horizon-mechanics §4.34). Cart mutations WITHOUT a user
    // interaction — the GWP auto-add/remove reconciler (assets/lg-cart-gwp.js) — have
    // no press to capture a title from, so they announce their own fully formed
    // (locale-driven) message via a document-level `lg:cart:announce` CustomEvent. This
    // announces `detail.message` directly through the SAME polite region; the
    // interaction-latch path above is untouched.
    // NOTE (merge 2026-09-10): the two seams are deliberately independent — the cancel
    // seam only clears the interaction latch (#pending); the direct seam never reads or
    // writes #pending. Both features share the ONE polite region (last write wins).
    document.addEventListener('lg:cart:announce', this.#onDirectAnnounce);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('pointerdown', this.#onInteract, true);
    document.removeEventListener('pointercancel', this.#onAnnounceCancel, true);
    document.removeEventListener('keydown', this.#onKeyDown, true);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQtyUpdate);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#onDiscountUpdate);
    document.removeEventListener('lg:cart:announce-cancel', this.#onAnnounceCancel);
    document.removeEventListener('lg:cart:restore-focus', this.#onRestoreFocus);
    document.removeEventListener('lg:cart:announce', this.#onDirectAnnounce);
    this.#guard?.stop();
    this.#guard = null;
    this.#pending = null;
  }

  #onAnnounceCancel = () => {
    this.#pending = null;
  };

  // ── Interaction latch ─────────────────────────────────────────────────────────────────────

  /**
   * Keyboard activation of a <button> fires keydown → click; only Enter and Space activate.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    this.#onInteract(event);
  };

  /**
   * @param {Event} event
   */
  #onInteract = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    if (!target?.closest) return;

    // Bundle removal (feat/lg-cart-bundle-grouping): a bespoke button, not the vendor
    // `on:click` remove. Capture the bundle title here — the group header is morphed away
    // by the removal — and announce after the update settles. The button already carries
    // every child line key, so the latch is verifiable against the dispatched event.
    const bundleBtn = /** @type {HTMLElement | null} */ (target.closest('[data-lg-bundle-remove]'));
    if (bundleBtn) {
      const bundleTitle = this.#titleForBundle(bundleBtn);
      const keys = (bundleBtn.dataset.lgBundleKeys || '').split(',').filter(Boolean);
      if (bundleTitle) this.#pending = { action: 'bundle', product: bundleTitle, keys };
      return;
    }

    // Remove buttons carry an `on:click` action of the shape "/onLineItemRemove/N".
    const removeBtn = target.closest('[on\\:click^="/onLineItemRemove"]');
    if (!removeBtn) return;
    const row = removeBtn.closest('.cart-line, [data-key]');
    const title = this.#titleForRow(removeBtn);
    if (!title) return;
    this.#pending = { action: 'remove', product: title, ...this.#neighbourhood(row) };
  };

  /**
   * QuantitySelectorUpdateEvent bubbles from the <quantity-selector> inside a cart line
   * (detail.quantity = new value). Fires the row title read BEFORE the cart AJAX morphs it.
   * @param {Event & { detail?: { quantity?: number, cartLine?: number } }} event
   */
  #onQtyUpdate = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    // Scope to cart lines only — the same event also fires on the PDP / quick-add quantity
    // selectors, which are not inside a cart row (and never trigger a cartLinesUpdate here).
    const row = target?.closest?.('.cart-line, [data-key]');
    if (!row) return;
    const title = this.#titleForRow(target);
    const qty = String(event.detail?.quantity ?? '');
    if (!title) return;
    // Quantity 0 is a removal — announce it as a removal, matching the user's mental model,
    // and re-home focus the same way the X button does.
    this.#pending =
      qty === '0'
        ? { action: 'remove', product: title, ...this.#neighbourhood(row) }
        : { action: 'update', product: title, quantity: qty, keys: this.#keysOf([row]) };
  };

  /**
   * Records which line is being removed AND where focus should go afterwards — computed NOW,
   * while the DOM is still intact, as KEYS (never element references: the morph replaces a row
   * whose id changes, §4.14). Bundle children are skipped: they render no remove control.
   * @param {Element | null} row
   * @returns {{ wasLast: boolean, keys: string[], nextKey: string | null, prevKey: string | null }}
   */
  #neighbourhood(row) {
    const inner = row?.closest(INNER) ?? document.querySelector(INNER);
    const rows = inner ? [.../** @type {NodeListOf<HTMLElement>} */ (inner.querySelectorAll('.cart-line[data-key]'))] : [];
    // A parent line takes its nested children with it (component-cart-items.js:168-172).
    const key = row instanceof HTMLElement ? row.dataset.key ?? '' : '';
    const doomed = rows.filter((r) => r.dataset.key === key || r.dataset.parentKey === key);
    const survivors = rows.filter((r) => !doomed.includes(r) && r.querySelector('.cart-line__remove'));
    const at = rows.indexOf(/** @type {HTMLElement} */ (row));
    return {
      // Fallback signal for "this removal empties the cart" when the settled payload carries no
      // item count (#isEmptyAfter prefers the server's count).
      wasLast: rows.length > 0 && rows.every((r) => doomed.includes(r)),
      keys: this.#keysOf(doomed),
      nextKey: survivors.find((r) => rows.indexOf(r) > at)?.dataset.key ?? null,
      prevKey: [...survivors].reverse().find((r) => rows.indexOf(r) < at)?.dataset.key ?? null,
    };
  }

  /**
   * @param {Element[]} rows
   * @returns {string[]}
   */
  #keysOf(rows) {
    return rows.map((r) => (r instanceof HTMLElement ? r.dataset.key ?? '' : '')).filter(Boolean);
  }

  /**
   * Reads the product title of the cart line containing `el`.
   * @param {Element | null} el
   * @returns {string}
   */
  #titleForRow(el) {
    const row = el?.closest('.cart-line, [data-key]');
    const titleEl = row?.querySelector('.cart-line__title');
    return titleEl?.textContent?.replace(/\s+/g, ' ').trim() || '';
  }

  /**
   * Reads the bundle title of the group containing `el` (the REMOVE BUNDLE
   * button). The `.cart-bundle__title` is `aria-hidden` for visuals, but its
   * textContent is the parent product title we announce.
   * @param {Element} el
   * @returns {string}
   */
  #titleForBundle(el) {
    const group = el.closest('[data-lg-bundle]');
    const titleEl = group?.querySelector('.cart-bundle__title');
    return titleEl?.textContent?.replace(/\s+/g, ' ').trim() || '';
  }

  // ── Consume ───────────────────────────────────────────────────────────────────────────────

  /**
   * The event param is REQUIRED: the bundle branch awaits `event.promise`
   * (CartLinesUpdateEvent carries the mutation promise) so a bundle removal is only announced
   * once it actually settles — never on a rejected update. `event.lines` carries the cart line
   * KEYS the mutation targets (ShopifyStandardEvent does `Object.assign(this, payload)`), which
   * is what lets us verify the latch belongs to THIS mutation.
   * @param {Event & { promise?: Promise<any>, lines?: Array<{ id?: string }>, action?: string }} event
   */
  #onCartUpdate = (event) => {
    const pending = this.#pending;
    this.#pending = null;
    if (!pending) return;

    // Stale-latch guard. A press that never became a mutation (pointer released off the button,
    // or a press the pointercancel listener above did not see), or an interaction-less mutation
    // arriving first (GWP reconcile §4.44, add-to-cart), would otherwise consume this latch and
    // announce a removal that did not happen. A latch that names its keys only matches an event
    // that names at least one of them; an event that names NO line keys (add-to-cart sends
    // `merchandiseId` only — product-form.js, lg-ritual-steps.js, lg-cart-gwp.js adds) can never
    // be ours. Drop the latch rather than guess. This narrows the stale-latch window; it does not
    // close it for a stale latch on a row whose own key a later mutation happens to touch.
    const ids = (event.lines ?? []).map((line) => line?.id).filter(Boolean);
    if (pending.keys.length && !pending.keys.some((key) => ids.includes(key))) return;

    const promise = event?.promise;
    const settled = promise && typeof promise.then === 'function' ? promise : null;

    if (pending.action !== 'update') {
      this.#guardFocus({
        removedKeys: pending.keys,
        nextKey: pending.nextKey ?? null,
        prevKey: pending.prevKey ?? null,
        settled,
      });
    }

    // Bundle removal — announce only once the mutation actually settles (the removal rejects
    // on a network failure, and we must not claim success).
    if (pending.action === 'bundle') {
      // Shares the focus ladder, so a bundle that was the last line also lands on the empty-state
      // CTA — say "Your bag is empty" here too (lg-cart-bundle.js resolves detail.itemCount).
      const pick = (/** @type {any} */ result) =>
        this.#isEmptyAfter(result, pending) ? this.dataset.msgBundleRemovedEmpty : this.dataset.msgBundleRemoved;
      const say = (/** @type {any} */ result) =>
        this.#announce((pick(result) || '').replace('__PRODUCT__', () => pending.product));
      if (settled) settled.then(say).catch(() => {});
      else say(null);
      return;
    }

    if (pending.action === 'remove') {
      const say = (/** @type {any} */ result) => {
        const msg = this.#isEmptyAfter(result, pending) ? this.dataset.msgRemovedEmpty : this.dataset.msgRemoved;
        this.#announce((msg || '').replace('__PRODUCT__', () => pending.product));
      };
      // Last line removed → the empty state replaces the list and focus lands on the CTA, not the
      // "Your bag is empty" heading, so the message says it. ONLY that case waits for the server
      // (to read the settled item count, and to stay silent on a rejected update); every other
      // removal announces immediately, keeping the measured 15–50ms timing.
      if (pending.wasLast && settled) settled.then(say).catch(() => {});
      else say(null);
      return;
    }
    const text = (this.dataset.msgUpdated || '')
      .replace('__PRODUCT__', () => pending.product)
      .replace('__QTY__', () => pending.quantity ?? '');
    this.#announce(text);
  };

  /**
   * Discount apply / remove (a11y audit B3). Unlike the lines path there is no latch to carry:
   * the event names the NEW code set and the DOM still holds the OLD pills at dispatch time, so
   * the delta is computable here. cart-discount.js resolves the promise and then morphs
   * SYNCHRONOUSLY (section-renderer.js morphSection has no await), so a `.then()` runs
   * post-morph — but only on the paths that morph at all: an invalid code and an inapplicable
   * shipping code resolve and return WITHOUT morphing. We therefore confirm the outcome against
   * the settled DOM instead of trusting the event, and stay silent when it did not take (the
   * failure is already announced by the role="alert" error region, lg-cart-summary.liquid:186).
   * @param {Event & { promise?: Promise<any>, discountCodes?: Array<{ code?: string }> }} event
   */
  #onDiscountUpdate = (event) => {
    const before = this.#appliedCodes();
    const after = (event.discountCodes ?? []).map((d) => d?.code).filter(Boolean);
    const added = after.find((code) => !before.includes(/** @type {string} */ (code)));
    const dropped = before.find((code) => !after.includes(code));
    if (!added && !dropped) return;

    const promise = event?.promise;
    if (!promise || typeof promise.then !== 'function') return;

    promise
      .then(() => {
        // One frame after the synchronous morph, so the settled DOM is what we read.
        requestAnimationFrame(() => {
          const now = this.#appliedCodes();
          if (added && now.includes(/** @type {string} */ (added))) {
            this.#guardFocus({
              mode: 'discount-applied',
              removedKeys: [],
              discountCode: /** @type {string} */ (added),
              settled: Promise.resolve(),
            });
            this.#announce((this.dataset.msgDiscountApplied || '').replace('__CODE__', () => /** @type {string} */ (added)));
          } else if (dropped && !now.includes(dropped)) {
            this.#guardFocus({ mode: 'discount-removed', removedKeys: [], settled: Promise.resolve() });
            this.#announce((this.dataset.msgDiscountRemoved || '').replace('__CODE__', () => dropped));
          }
        });
      })
      .catch(() => {});
  };

  /** @returns {string[]} the codes currently rendered as applied pills. */
  #appliedCodes() {
    return [...document.querySelectorAll('.cart-discount__pill')]
      .map((pill) => (pill instanceof HTMLElement ? pill.dataset.discountCode ?? '' : ''))
      .filter(Boolean);
  }

  /**
   * assets/lg-cart-bundle.js delegates its post-removal focus restore here.
   * @param {Event} event
   */
  #onRestoreFocus = (event) => {
    const detail = /** @type {{ removedKeys?: string[] } | undefined} */ (/** @type {CustomEvent} */ (event).detail);
    this.#guardFocus({ removedKeys: detail?.removedKeys ?? [], settled: null });
  };

  /**
   * Direct-announce seam for interaction-less cart mutations (GWP auto-add/remove).
   * The dispatcher owns the full locale-driven message; we just pipe it to the
   * polite region. No #pending interplay — this path never reads a row title.
   * @param {Event} event
   */
  #onDirectAnnounce = (event) => {
    const detail = /** @type {{ message?: string } | undefined} */ (/** @type {CustomEvent} */ (event).detail);
    const message = detail?.message;
    if (typeof message === 'string' && message) this.#announce(message);
  };

  // ── Focus guard ───────────────────────────────────────────────────────────────────────────

  /**
   * Re-homes focus while a cart mutation tears down the control the user activated.
   *
   * Horizon mutates the drawer TWICE per removal — an optimistic local `row.remove()` (or, on
   * the last line, a `#empty-cart-template` swap) and then the server morph — and the morph can
   * blur a node it MOVES (morph.js:628-642 uses insertBefore, not moveBefore). A settle-once
   * restore therefore has no answer when the second mutation undoes the first. This guard stays
   * armed for the whole window and re-homes as often as needed, while two rules keep it from
   * fighting the user:
   *
   *   - ORPHAN-ONLY: it acts only when focus has fallen to <body> or onto a disconnected node.
   *     A shopper who tabbed elsewhere during the flight keeps their place (the inverse 2.4.3
   *     failure).
   *   - HANDS OFF ONCE MOVED: focus landing anywhere that is not the element we placed disarms
   *     the guard permanently for that mutation.
   *
   * @param {Object} config
   * @param {'lines' | 'discount-applied' | 'discount-removed'} [config.mode]
   * @param {string[]} config.removedKeys - rows going away; never a focus target.
   * @param {string | null} [config.nextKey] - preferred landing: the row taking its place.
   * @param {string | null} [config.prevKey] - second choice: the row above it.
   * @param {string | null} [config.discountCode] - `discount-applied`: land on this code's Remove.
   * @param {Promise<any> | null} config.settled - the mutation promise, when the caller has one.
   */
  #guardFocus({ mode = 'lines', removedKeys, nextKey = null, prevKey = null, discountCode = null, settled }) {
    // DELIBERATE EXCEPTION to the "re-locate, never hold a reference" rule this file argues two
    // screens above (§4.14): `inner` is captured once and held for the guard's lifetime. It is
    // safe here, and only here, because `inner` is the morph TARGET, not a node inside it. On the
    // drawer the morph is hydration-mode and reconciles the CHILDREN of
    // `[data-hydration-key="cart-drawer-inner"]` — the element itself is never replaced (§4.5);
    // on /cart it is full-mode but `inner` is likewise the stable container the renderer morphs
    // into (§4.43). Both surfaces were measured: the guard kept finding live targets through the
    // optimistic teardown AND the follow-up server morph, including the WebKit empty-branch run
    // where the morph blurred focus at t=639ms and the guard re-homed at t=1046ms — impossible if
    // `inner` had gone stale. Every node we actually FOCUS is still re-located per look, by key,
    // inside #focusTarget. If a future change makes the cart host replace this element rather than
    // its children, move this query inside `rehome()`.
    const inner = document.querySelector(INNER);
    if (!(inner instanceof HTMLElement)) return;

    // A newer mutation supersedes an older guard — never run two at once.
    this.#guard?.stop();

    const removed = new Set(removedKeys);
    // The control the user activated. It is still focused and still connected when the guard
    // arms (the mutation is dispatched BEFORE the row is torn down), so it must not be mistaken
    // for "the user moved focus" — that misread stands the guard down before it ever runs.
    const origin = document.activeElement;
    let placed = /** @type {HTMLElement | null} */ (null);
    let alive = true;

    const stop = () => {
      if (!alive) return;
      alive = false;
      observer.disconnect();
      clearTimeout(hardStop);
      if (this.#guard === handle) this.#guard = null;
    };

    const rehome = () => {
      if (!alive) return;
      const active = document.activeElement;
      const orphaned = !active || active === document.body || !active.isConnected;
      if (!orphaned) {
        // Focus is on a live element. Three cases: it is still the control being removed
        // (wait — the teardown has not happened yet); it is where WE put it (wait — a later
        // morph may still blur it); or the user moved it somewhere of their own choosing, in
        // which case stand down for good rather than yank them back.
        if (active !== placed && active !== origin) stop();
        return;
      }
      const target = this.#focusTarget(inner, mode, removed, nextKey, prevKey, discountCode);
      if (!target) return;
      if (!target.hasAttribute('tabindex') && target.tabIndex < 0) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      placed = target;
    };

    const observer = new MutationObserver(rehome);
    observer.observe(inner, { childList: true, subtree: true });
    const hardStop = setTimeout(stop, GUARD_MS);
    const handle = { stop };
    this.#guard = handle;

    // The optimistic removal can land before any observer callback under reduced motion
    // (component-cart-items.js:191) — and startViewTransition degrades to a synchronous call
    // (utilities.js:120-127) — so take a first look on the next frame regardless.
    requestAnimationFrame(rehome);
    // Close the window a beat after the mutation settles rather than burning the full 2s.
    settled
      ?.then(() => {
        setTimeout(() => {
          rehome();
          stop();
        }, SETTLE_TAIL_MS);
      })
      .catch(() => stop());
  }

  /**
   * Picks the most meaningful surviving element to receive focus. Order is deliberate: stay in
   * the list where the user was working; when the list is gone, the empty state's "Continue
   * shopping" CTA (owner 2026-10-15), then the dialog's own label; and only then the drawer's
   * close control.
   * @param {HTMLElement} inner
   * @param {'lines' | 'discount-applied' | 'discount-removed'} mode
   * @param {Set<string>} removed
   * @param {string | null} nextKey
   * @param {string | null} prevKey
   * @param {string | null} discountCode
   * @returns {HTMLElement | null}
   */
  #focusTarget(inner, mode, removed, nextKey, prevKey, discountCode) {
    /** @param {string} selector */
    const pick = (selector) => {
      const el = inner.querySelector(selector);
      return el instanceof HTMLElement && el.offsetParent !== null ? el : null;
    };

    if (mode === 'discount-applied' && discountCode) {
      // The code's own Remove control — the counterpart of the Apply button that vanished.
      const applied = pick(`.cart-discount__pill[data-discount-code="${CSS.escape(discountCode)}"] button`);
      if (applied) return applied;
    }
    if (mode === 'discount-removed') {
      // Removing the last code flips the promo block back to its <details> disclosure
      // (lg-cart-summary.liquid:113); with codes left, stay on the list.
      const toggle = pick('.cart-promo__disclosure > summary') ?? pick('.cart-discount__pill button');
      if (toggle) return toggle;
    }
    // Both discount modes STOP here. Falling through to the line-removal ladder below would put
    // focus on a cart line's Remove button — a DESTRUCTIVE control — after a non-destructive
    // promo interaction, so a shopper pressing Enter again would delete a product. The measured
    // path always finds the pill above, so this is latent; it is still the wrong place to land.
    // Returning null leaves focus where the browser put it, which is the conservative failure.
    if (mode !== 'lines') return null;

    // 1/2 — the row that took the removed row's place, else the row above it.
    for (const key of [nextKey, prevKey]) {
      if (!key) continue;
      const el = pick(`.cart-line[data-key="${CSS.escape(key)}"] .cart-line__remove`);
      if (el) return el;
    }

    // 3 — any surviving remove control that is not on a doomed row.
    for (const el of inner.querySelectorAll('[data-lg-bundle-remove], .cart-line__remove')) {
      if (!(el instanceof HTMLElement) || el.offsetParent === null) continue;
      const row = el.closest('[data-key]');
      if (row instanceof HTMLElement && removed.has(row.dataset.key ?? '')) continue;
      return el;
    }

    // 4 — the empty state's "Continue shopping" CTA (owner decision 2026-10-15): when the last
    // line goes, focus lands on the one useful next action rather than the heading. It is a real
    // link, so no tabindex is needed. The removal message says "Your bag is empty" (#isEmptyAfter),
    // since focus no longer passes the empty-state heading.
    const cta = pick('.cart-empty__cta');
    if (cta) return cta;

    // 5 — the dialog's accessible name. On the empty branch this is the empty-state title,
    // re-created by the #empty-cart-template clone from the SAME Liquid capture the server
    // renders (lg-cart.liquid:61-85 / :211-213), so it is id-matched and survives the follow-up
    // morph. Programmatically focusable only; the caller injects tabindex="-1".
    // Visibility-guarded like steps 1-4 and 6: a heading in a closed drawer (or a display:none
    // subtree) is not a place to put focus. On /cart there is no dialog, so only offsetParent
    // applies there.
    const heading = document.getElementById('cart-drawer-heading');
    if (heading instanceof HTMLElement && heading.offsetParent !== null) return heading;

    // 6 — last resort: the drawer's own close control, so focus is at least inside the dialog.
    const close = document.querySelector('dialog[open] .theme-drawer__close-button');
    return close instanceof HTMLElement ? close : null;
  }

  /**
   * Did this removal empty the cart? Prefers the settled payload's count —
   * component-cart-items.js and lg-cart-bundle.js both resolve { detail: { itemCount } } —
   * and falls back to the latch's own view of the DOM (`wasLast`).
   * @param {any} result - the settled mutation payload, if any
   * @param {Pending} pending
   */
  #isEmptyAfter(result, pending) {
    const count = result?.detail?.itemCount;
    return typeof count === 'number' ? count === 0 : pending.wasLast === true;
  }

  // ── Announce ──────────────────────────────────────────────────────────────────────────────

  /**
   * @param {string} text
   */
  #announce(text) {
    if (!text) return;
    const region = this.refs.liveRegion;
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = text;
    });
  }
}

if (!customElements.get('lg-cart-announce')) {
  customElements.define('lg-cart-announce', LgCartAnnounce);
}
